/**
 * Historial — DESKTOP.
 *
 * Una tabla plana con TODAS las series de todas las sesiones, editables en el lugar. Es la forma
 * en que una corrección se hace rápido cuando ya pasó tiempo: se busca con la vista, se corrige el
 * número en la celda, se sigue. En el celular eso es imposible y por eso ahí hay lista y hoja.
 *
 * El selector de ejercicio de cada fila es la reasignación de §4.2: cambiar el ejercicio de un
 * peso ya guardado sin borrarlo.
 */

import { useState, type ReactNode } from 'react';
import { fechaLarga, formatearDuracion, formatearNumero, type Serie } from '@mg/core';

import { useHistorial, type SesionDetallada } from '../features/useHistorial.ts';
import { Aviso, Campo, Insignia, Vacio } from '../ui/componentes.tsx';

export function HistorialDesktop(): ReactNode {
  const h = useHistorial();
  const [filtro, setFiltro] = useState('');

  if (h.datos.cargando) return <Vacio>Cargando…</Vacio>;
  if (h.sesiones.length === 0 && h.arrastradas.length === 0) {
    return <Vacio>Todavía no hay sesiones registradas.</Vacio>;
  }

  const visibles = filtro
    ? h.sesiones.filter(
        (s) =>
          s.sesion.fecha.includes(filtro) ||
          (s.nombreDia ?? '').toLowerCase().includes(filtro.toLowerCase()) ||
          s.ejercicios.some((e) => e.ejercicio.nombre.toLowerCase().includes(filtro.toLowerCase())),
      )
    : h.sesiones;

  return (
    <>
      <div className="fila fila--entre">
        <div style={{ width: 320 }}>
          <Campo
            etiqueta="Buscar"
            texto
            placeholder="fecha, ejercicio o día de rutina"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
        <span className="nota--fina">
          {visibles.length} de {h.sesiones.length} sesiones · las celdas de peso y reps se editan en
          el lugar
        </span>
      </div>

      {h.error && <Aviso tono="importante">{h.error}</Aviso>}

      {/* Lo arrastrado no es historial —todavía no pasó— pero es la consecuencia de un omitido y
          desaparecía de la vista al sacar las sesiones planificadas de la lista. */}
      {h.arrastradas.length > 0 && (
        <section className="tarjeta">
          <span className="eyebrow">Todavía pendiente</span>
          <div className="columna" style={{ gap: 6, marginTop: 'var(--e2)' }}>
            {h.arrastradas.map((a) => (
              <p key={`${a.ejercicio.id}-${a.hacia}`} className="nota--fina">
                <Insignia tono="ambar">arrastrada</Insignia>{' '}
                <span className="lectura">{a.series}</span> series de {a.ejercicio.nombre}: vienen de la
                sesión anterior{a.desde ? ` (${fechaLarga(a.desde)})` : ''} y esperan en la del{' '}
                {fechaLarga(a.hacia)}.
              </p>
            ))}
          </div>
        </section>
      )}

      {visibles.map((s) => (
        <BloqueSesion key={s.sesion.id} s={s} h={h} />
      ))}
    </>
  );
}

function BloqueSesion({
  s,
  h,
}: {
  s: SesionDetallada;
  h: ReturnType<typeof useHistorial>;
}): ReactNode {
  const [moviendo, setMoviendo] = useState(false);
  const [fecha, setFecha] = useState(s.sesion.fecha);
  const movida = h.movida?.sesionId === s.sesion.id ? h.movida : null;

  return (
    <section className="tarjeta" style={{ padding: 0 }}>
      <div className="fila fila--entre" style={{ padding: 'var(--e4)' }}>
        <div>
          <div className="fila" style={{ gap: 8 }}>
            <strong style={{ fontSize: 16 }}>{fechaLarga(s.sesion.fecha)}</strong>
            {s.sesion.estado === 'omitida' && <Insignia tono="alerta">omitida</Insignia>}
            {s.sesion.estado === 'en_curso' && <Insignia tono="ambar">en curso</Insignia>}
          </div>
          <p className="nota--fina">
            {s.nombreDia ?? 'Sin día de rutina'} · {s.seriesHechas} series ·{' '}
            {formatearNumero(s.volumenKg / 1000, 2)} t de volumen
            {s.cardio && (
              <>
                {' · cardio '}
                {formatearDuracion(s.cardio.duracion_seg)} / {formatearNumero(s.cardio.distancia_km, 2)} km
                {s.cardio.nivel_resistencia === null ? (
                  <span style={{ color: 'var(--ambar)' }}> · sin nivel, no comparable</span>
                ) : (
                  ` · nivel ${s.cardio.nivel_resistencia}`
                )}
              </>
            )}
          </p>
        </div>

        {moviendo ? (
          <div className="fila" style={{ gap: 8 }}>
            <input
              className="campo"
              type="date"
              style={{ minHeight: 36, width: 160 }}
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
            <button
              type="button"
              className="boton boton--chico boton--principal"
              disabled={fecha === s.sesion.fecha}
              onClick={async () => {
                await h.mover(s.sesion, fecha);
                setMoviendo(false);
              }}
            >
              Mover
            </button>
            <button type="button" className="boton boton--chico boton--fantasma" onClick={() => setMoviendo(false)}>
              Cancelar
            </button>
          </div>
        ) : (
          <button type="button" className="boton boton--chico boton--fantasma" onClick={() => setMoviendo(true)}>
            Mover de fecha
          </button>
        )}
      </div>

      {/* §4.2 pide que el recálculo se explique. Mover y no decir nada deja al usuario sin saber
          si el plan se acomodó o si acaba de perder una sesión. */}
      {movida && (
        <div style={{ padding: '0 var(--e4) var(--e4)' }}>
          <Aviso tono="info" titulo="Qué cambió">
            <span style={{ display: 'block' }}>La sesión quedó en el {fechaLarga(movida.fecha)}.</span>
            {movida.cambios.length === 0 ? (
              <span style={{ display: 'block' }}>En el plan no cambió nada.</span>
            ) : (
              movida.cambios.map((x) => (
                <span key={x.texto} style={{ display: 'block' }}>
                  {x.texto}
                </span>
              ))
            )}
            <button
              type="button"
              className="boton boton--chico boton--fantasma"
              style={{ marginTop: 8 }}
              onClick={h.limpiarMovida}
            >
              Entendido
            </button>
          </Aviso>
        </div>
      )}

      <div className="desborda-x">
        <table className="tabla">
          <thead>
            <tr>
              <th style={{ minWidth: 190 }}>Ejercicio</th>
              <th className="num" style={{ width: 60 }}>
                Serie
              </th>
              <th className="num" style={{ width: 110 }}>
                Peso
              </th>
              <th className="num" style={{ width: 90 }}>
                Reps
              </th>
              {/* Sin una sola serie con RIR la columna era una fila de guiones. */}
              {s.tieneRir && (
                <th className="num" style={{ width: 80 }}>
                  RIR
                </th>
              )}
              <th className="num" style={{ width: 110 }}>
                Volumen
              </th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {s.ejercicios.flatMap((e) =>
              e.series.map((serie) => (
                <FilaSerie
                  key={serie.id}
                  serie={serie}
                  nombre={e.ejercicio.nombre}
                  conRir={s.tieneRir}
                  h={h}
                />
              )),
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FilaSerie({
  serie,
  nombre,
  conRir,
  h,
}: {
  serie: Serie;
  nombre: string;
  conRir: boolean;
  h: ReturnType<typeof useHistorial>;
}): ReactNode {
  const [peso, setPeso] = useState(serie.peso);
  const [reps, setReps] = useState(serie.reps);
  const sucia = peso !== serie.peso || reps !== serie.reps;

  return (
    <tr style={{ opacity: serie.completada ? 1 : 0.55 }}>
      <td>
        <select
          className="campo campo--texto"
          style={{ minHeight: 32, fontSize: 13, border: 'none', background: 'transparent', padding: 0 }}
          value={serie.ejercicio_id}
          onChange={(e) => void h.reasignar(serie, e.target.value)}
          aria-label={`Ejercicio de la serie ${serie.numero_serie} (${nombre})`}
          title="Cambiar el ejercicio sin borrar el registro"
        >
          {h.datos.ejercicios.map((x) => (
            <option key={x.id} value={x.id}>
              {x.nombre}
            </option>
          ))}
        </select>
      </td>

      <td className="num apagado">{serie.numero_serie}</td>

      <td>
        {serie.completada ? (
          <input
            className="campo"
            style={{ minHeight: 32, textAlign: 'right' }}
            inputMode="decimal"
            value={formatearNumero(peso, 2)}
            onChange={(e) => {
              const n = Number(e.target.value.replace(',', '.'));
              if (Number.isFinite(n)) setPeso(n);
            }}
            onBlur={() => {
              if (sucia) void h.editar(serie, { peso, reps });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void h.editar(serie, { peso, reps });
            }}
            aria-label={`Peso de la serie ${serie.numero_serie} de ${nombre}`}
          />
        ) : (
          <span className="apagado" style={{ display: 'block', textAlign: 'right' }}>
            —
          </span>
        )}
      </td>

      <td>
        {serie.completada ? (
          <input
            className="campo"
            style={{ minHeight: 32, textAlign: 'right' }}
            inputMode="numeric"
            value={reps}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isInteger(n)) setReps(n);
            }}
            onBlur={() => {
              if (sucia) void h.editar(serie, { peso, reps });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void h.editar(serie, { peso, reps });
            }}
            aria-label={`Reps de la serie ${serie.numero_serie} de ${nombre}`}
          />
        ) : (
          <span className="apagado" style={{ display: 'block', textAlign: 'right' }}>
            —
          </span>
        )}
      </td>

      {conRir && <td className="num apagado">{serie.rir ?? '—'}</td>}

      <td className="num">
        {serie.completada ? `${formatearNumero(serie.reps * serie.peso, 0)} ${serie.unidad}` : '—'}
      </td>

      <td>
        {serie.completada ? (
          sucia ? (
            <Insignia tono="ambar">sin guardar</Insignia>
          ) : (
            <span className="apagado nota--fina">hecha</span>
          )
        ) : serie.motivo_omision ? (
          <Insignia tono="alerta">{h.nombreMotivo(serie.motivo_omision)}</Insignia>
        ) : serie.arrastrada_de_sesion_id ? (
          <span className="columna" style={{ gap: 2, alignItems: 'flex-start' }}>
            <Insignia tono="ambar">arrastrada</Insignia>
            <span className="apagado nota--fina">viene de la sesión anterior</span>
          </span>
        ) : (
          <span className="apagado nota--fina">pendiente</span>
        )}
      </td>
    </tr>
  );
}
